/**
 * The application's route table.
 *
 * Adding a feature is adding a line here, and nothing else in the application
 * knows what routes exist. `collectRoutes` refuses two features claiming one
 * path at startup rather than letting import order decide which one wins and
 * which one silently disappears.
 *
 * Separate from `app.tsx` so the layout can read the table to build its
 * navigation without importing the component that renders it.
 *
 * A `.tsx` file, because two of the injections below are components: the
 * customer file's notes panel belongs to `support` and its tariff lines to
 * `referential`, a feature may not import a sibling feature, and this file is
 * the one place allowed to know both exist.
 */

import { HOME_PATH, arrangeNavigation, collectRoutes } from "@hardauto/core-router";
import { errorRoutes } from "@hardauto/feature-errors";
import { IAM_PATHS, iamRoutes } from "@hardauto/feature-iam";
import { adminRoutes } from "@hardauto/feature-admin";
import { billingRoutes } from "@hardauto/feature-billing";
import { filesRoutes } from "@hardauto/feature-files";
import { notificationRoutes } from "@hardauto/feature-notifications";
import { settingsRoutes } from "@hardauto/feature-settings";
import { catalogRoutes } from "@hardauto/feature-catalog";
import { clientsRoutes } from "@hardauto/feature-clients";
import { CONNECTOR_FILES_PATHS, connectorFilesRoutes } from "@hardauto/feature-connector-files";
import { integrationRoutes } from "@hardauto/feature-integration";
import { onboardingRoutes } from "@hardauto/feature-onboarding";
import { OrdersApi, ordersRoutes } from "@hardauto/feature-orders";
import { ReferentialApi, referentialRoutes } from "@hardauto/feature-referential";
import { NotesPanel, supportRoutes } from "@hardauto/feature-support";

import { client } from "./api";
import { config } from "./config";
import { NAV_AREAS } from "./nav-areas";
import { HomeScreen } from "./home";
import { StockCategoryFilter } from "./stock-category-filter";
import { loadStockStructure, structureChanged } from "./structure";
import { useCatalogScope, useScopeClient } from "./workspace-context";

const collected = collectRoutes([
  /**
   * `/` is contributed by the application, not by a feature.
   *
   * It has to exist: `safeReturnTo` falls back to it, so a sign-in with no
   * `returnTo` would otherwise land on "that page does not exist" — which is
   * the first thing a new user of this template would see.
   *
   * It is the home page, "Accueil", first in the rail. No permission: every
   * signed-in principal has a home, and an anonymous one is sent to sign-in
   * by the screen itself.
   */
  {
    id: "app",
    routes: [
      {
        path: HOME_PATH,
        component: HomeScreen,
        layout: "app",
        nav: { labelKey: "backoffice:menu.home", section: "main", order: 0 },
      },
    ],
  },
  iamRoutes(client),
  errorRoutes({ signInHref: IAM_PATHS.signIn }),
  adminRoutes(client),
  settingsRoutes(client),
  filesRoutes(client, config.maxUploadBytes),
  notificationRoutes(client),
  // An edit to the structure re-reads the copy the context band keeps.
  referentialRoutes(client, {
    onStructureChanged: structureChanged,
    // Read-only here: the referential's screens narrow to the context, they
    // do not change it — that stays the header's job.
    useScope: () => {
      const { scope } = useCatalogScope();
      return { companyCode: scope.companyCode, siteCode: scope.siteCode, active: scope.active };
    },
    // « Articles par dépôt » narrows by the web tree's categories. The tree is the
    // catalogue's, so it is handed over here, where two features may meet.
    CategoryFilter: StockCategoryFilter,
  }),
  // The depots and the tariffs the availability screen chooses between.
  // Injected here rather than imported by `feature-catalog`: both belong to
  // the referential, a feature may not import a sibling feature, and this
  // file is the composition root — the four lines are where the coupling can
  // be read.
  catalogRoutes(client, {
    // The consultation reads and changes the session's context, which the
    // header shows; the structure is the referential's.
    useScope: useCatalogScope,
    loadStructure: loadStockStructure,
    // Prices on the list are TTC, at the VAT rate of the ordering parameters
    // — `orders` owns it, and this is where two features may meet.
    loadTaxRate: async () => (await new OrdersApi(client).policy()).tax_rate_pct,
    // The same depots the context offers: active, and not a hidden company's.
    loadSites: async () =>
      (await loadStockStructure()).sites.map((site) => ({ code: site.code, name: site.name })),
    loadScales: async () => {
      const { items } = await new ReferentialApi(client).listScales(false);
      return items.map((scale) => ({ code: scale.code, label: scale.label }));
    },
  }),
  // The sales lists narrow to the customer the context is on.
  ordersRoutes(client, { useClient: useScopeClient }),
  billingRoutes(client),
  supportRoutes(client),
  onboardingRoutes(client),
  integrationRoutes(client),
  // The file connector's own screen. Listed beside the contract's rather
  // than inside it: the backend module and this package are removed
  // together, and neither feature imports the other.
  connectorFilesRoutes(client),
  // `loadTariff` is injected here rather than imported by `feature-clients`:
  // the tariff lines belong to the referential, a feature may not import a
  // sibling feature, and this file is the composition root — the one place
  // allowed to know both exist. The adapter is four lines and lives where the
  // coupling can be read.
  clientsRoutes(client, {
    // The customer file's notes tab. Injected here rather than imported by
    // `feature-clients`: §6.12's notes belong to `support`, a feature may not
    // import a sibling feature, and this file is the composition root — the
    // one place allowed to know both exist.
    notesPanel: (clientId: string) => (
      <NotesPanel client={client} subjectKind="client" subjectId={clientId} />
    ),
    loadTariff: async (scaleCode) => {
      const referential = new ReferentialApi(client);
      const { items } = await referential.listRules(scaleCode);
      return items.map((rule) => ({
        id: rule.id,
        familyCode: rule.family_code,
        articleReference: rule.article_reference,
        siteCode: rule.site_code,
        discountPct: rule.discount_pct,
        fixedPrice: rule.fixed_price,
        validFrom: rule.valid_from,
        validTo: rule.valid_to,
        visibleOnPortal: rule.visible_on_portal,
      }));
    },
  }),
]);

/**
 * What only the owner may open: the whole Administration area — structure, users, roles,
 * settings, the catalogue's configuration, the ERP connection. Held in one place, here,
 * rather than on a dozen routes in as many features, so the menu and a typed address
 * agree and nobody has to remember to add the line to the next screen.
 *
 * `access.role.read` is the owner's marker: only the owner holds it. A role that is given
 * it in the admin screen is, in effect, an administrator.
 *
 * The file exchange stays open to anyone who may export, whose own permission it asks for;
 * what it shows them is only the export. Hiding is not the protection: every endpoint
 * behind these screens still checks its own permission.
 */
const OWNER_PERMISSION = "access.role.read";
const OPEN_TO_EVERYONE_WITH_THEIR_OWN_PERMISSION = new Set<string>([
  CONNECTOR_FILES_PATHS.exchange,
]);

function restrictAdministration(routes: ReturnType<typeof collectRoutes>) {
  const nodes = arrangeNavigation(
    routes.filter((route): route is typeof route & { nav: NonNullable<typeof route.nav> } =>
      Boolean(route.nav),
    ),
    NAV_AREAS,
  );
  const administration = nodes.find(
    (node) => node.kind === "area" && node.area.id === "administration",
  );
  const owned = new Set(
    administration && administration.kind === "area"
      ? administration.entries.map((entry) => entry.path)
      : [],
  );
  return routes.map((route) => {
    if (!owned.has(route.path) || OPEN_TO_EVERYONE_WITH_THEIR_OWN_PERMISSION.has(route.path)) {
      return route;
    }
    const existing = route.requirement?.permissions ?? [];
    return {
      ...route,
      requirement: {
        ...route.requirement,
        authenticated: true,
        // A route asking for "any of" several permissions becomes the owner's alone: the
        // owner holds them all.
        permissions:
          route.requirement?.mode === "any" ? [OWNER_PERMISSION] : [...existing, OWNER_PERMISSION],
        mode: "all" as const,
      },
    };
  });
}

export const routeTable = restrictAdministration(collected);
